/**
 * English.
 *
 * Typed against the Italian dictionary, so a key added there and forgotten here fails
 * the build rather than showing a dotted path to a user.
 *
 * British English, and the domain terms stay in the register the trade actually uses:
 * "site" not "construction site", "commessa" becomes "job" not "order", and the Italian
 * legal instruments (DURC, UNILAV, POS, DiCo, PES/PAV) keep their names because they
 * have no English equivalent and renaming them would make them unsearchable.
 */

import type { Dictionary } from './index';

export const en: Dictionary = {
  common: {
    allEmployees: 'All employees',
    typeToSearch: 'Type to search…',
    noMatches: 'No matches',
    searchPeople: 'Search by name, email or role…',
    networkError: 'Could not reach the server. Check your connection and try again.',
    databaseStarting: 'The system is starting up. Try again in a few seconds.',
    tryAgain: 'Try again',
    waking: 'Starting up, one moment…',
    charsLeftOne: '{count} more character.',
    charsLeftMany: '{count} more characters.',
    notAvailable: 'n/a',
    unknownError: 'Unknown error.',
    somethingWrong: 'Something went wrong',
    backToList: 'Back to the list',
    yes: 'Yes',
    no: 'No',
    apply: 'Apply',
    start: 'Start',
    end: 'End',
    reasonRequired: 'Reason (required)',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    areYouSure: 'Are you sure?',
    notice: 'Something went wrong',
    ok: 'OK',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    open: 'Open',
    back: 'Back',
    all: 'All',
    none: 'None',
    loading: 'Loading…',
    saving: 'Saving…',
    search: 'Search',
    details: 'Details',
    download: 'Download',
    from: 'From',
    to: 'To',
    day: 'Day',
    today: 'Today',
    tomorrow: 'Tomorrow',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisWeek: 'This week',
    onlyToday: 'Today only',
    employee: 'Employee',
    employees: 'Employees',
    site: 'Site',
    sites: 'Sites',
    job: 'Job',
    jobs: 'Jobs',
    company: 'Company',
    date: 'Date',
    time: 'Time',
    status: 'Status',
    notes: 'Notes',
    notesOptional: 'Notes (optional)',
    optional: 'optional',
    required: 'required',
    hours: 'Hours',
    error: 'Something went wrong.',
    saveError: 'Could not save.',
    retry: 'Try again',
    noResults: 'No results.',
    noResultsHint: 'Try changing the search or the filters.',
    clearFilters: 'Clear filters',
    select: 'Select…',
    add: 'Add…',
    creating: 'Creating…',
    description: 'Description',
    priority: 'Priority',
    normal: 'Normal',
    onlyOffice: 'Office only',
    allStaff: 'All staff',
    assignedEngineers: 'Assigned engineers',
    noJob: 'No job',
    outsideArea: 'Outside area',
    daysLeft: '{count} d',
    daysAgo: '{count} d ago',
    address: 'Address',
    city: 'City',
    latitude: 'Latitude',
    longitude: 'Longitude',
    postcode: 'Postcode',
    accessNotes: 'Access notes',
    manage: 'Manage',
    assign: 'Assign',
    remove: 'Remove',
    field: 'Field',
    newValue: 'New value',
    reason: 'Reason',
    reasonGiven: 'Reason given',
    backToJobs: 'Back to jobs',
    backToShifts: 'Back to shifts',
    shift: 'Shift',
    clockInTime: 'Clock-in time',
    clockOutTime: 'Clock-out time',
    noSiteLinked: 'No site linked.',
    noShiftsRecorded: 'No shifts recorded.',
    noDocumentsAttached: 'No documents attached.',
    noTeam: 'No engineer assigned.',
    noAdjustments: 'No adjustments recorded.',
    adjust: 'Adjust shift',
  },

  nav: {
    calendar: 'Calendar',
    leave: 'Leave and permits',
    more: 'More',
    openMenu: 'Open the menu',
    closeMenu: 'Close the menu',
    inventory: 'Inventory',
    vehicles: 'Vehicles',
    recycleBin: 'Recycle bin',
    messages: 'Messages',
    portal: 'Internal portal',
    dashboard: 'Dashboard',
    groupOperations: 'Operations',
    groupRecords: 'Records',
    groupCompliance: 'Compliance',
    groupSystem: 'System',
    shifts: 'Shifts',
    assignments: 'Assignments',
    payroll: 'Hours and pay',
    employees: 'Employees',
    sites: 'Sites',
    jobs: 'Jobs',
    alerts: 'Expiries',
    compliance: 'Required documents',
    documents: 'Document library',
    audit: 'Activity log',
    settings: 'Settings',
  },

  theme: {
    label: 'Theme',
    system: 'System',
    light: 'Light',
    dark: 'Dark',
  },

  locale: {
    label: 'Language',
    it: 'Italiano',
    en: 'English',
    ar: 'العربية',
  },

  table: {
    openRow: 'Open',
    search: 'Search',
    searchPlaceholder: 'Search…',
    sortBy: 'Sort by',
    ascending: 'ascending',
    descending: 'descending',
    view: 'View',
    viewTable: 'Table',
    viewCards: 'Cards',
    resultsOne: '{count} result',
    resultsMany: '{count} results',
    filteredFrom: 'of {total}',
  },

  dashboard: {
    title: 'Dashboard',
    onDutyNow: 'On duty now',
    jobsToday: 'Jobs today',
    toReview: 'To review',
    toReviewHint: 'flagged shifts, last 30 days',
    unassigned: 'Unassigned',
    unassignedHint: 'scheduled jobs with no engineer',
    nobodyOnDuty: 'Nobody is currently on duty.',
    shiftsToReview: 'Shifts to review',
    noShiftsToReview: 'No shifts need reviewing.',
    expiriesToHandle: 'Expiries to handle',
    noExpiries: 'Nothing to handle. Every document is in order.',
    seeAll: 'See all',
    jobsWithoutEngineer: 'Jobs with no engineer',
    allJobsAssigned: 'Every scheduled job has an engineer assigned.',
    allClear: 'All clear',
    allClearHint: 'Nothing needs attention.',
  },

  shifts: {
    jobAndSite: 'Job · site',
    noJob: 'No job',
    inPosition: 'Clock-in position',
    outPosition: 'Clock-out position',
    openInMap: 'Open in map',
    siteArea: 'Site area',
    needsReview: 'To review',
    review: 'Mark as reviewed',
    reviewTitle: 'Review this shift',
    reviewExplainer:
      'Confirm the shift is correct as recorded. The flags stay visible — they are the evidence of what happened — but the shift leaves the queue and counts towards hours.',
    reviewNote: 'Note (optional)',
    reviewNotePlaceholder: 'e.g. confirmed by phone with the employee.',
    reviewedBy: 'Reviewed by {name}, {date}',
    withdrawReview: 'Undo review',
    reviewedBadge: 'Reviewed',
    queueEmpty: 'No shifts to review.',
    onlyNeedsReview: 'Only to review',
    breakShort: 'break {count} min',
    recordedDuration: 'Recorded duration',
    inProgressSuffix: '· in progress',
    deviceEventId: 'Device event ID',
    locationHint1:
      'A location is recorded only at clock-in and clock-out, never during the shift.',
    locationHint2:
      'The outcome is computed by the server from the raw coordinates, not by the device.',
    adjustments: 'Changes',
    noAdjustmentsHint: 'No changes. The shift is exactly as it was recorded.',
    adjustmentSaved: 'Change saved.',
    saveAdjustment: 'Save change',
    breakMinutes: 'Break (minutes)',
    correctHint: 'Every change is recorded with its reason and shown to the employee.',
    clockIn: 'Clock-in time',
    clockOut: 'Clock-out time',
    job: 'Job',
    locationData: 'Location data',
    inOutcome: 'Clock-in · outcome',
    inDistance: 'Clock-in · distance',
    outOutcome: 'Clock-out · outcome',
    outDistance: 'Clock-out · distance',
    noGps: 'No GPS',
    correct: 'Correct this shift',
    reasonPlaceholder: 'e.g. the employee confirmed the times by phone',
    title: 'Shifts',
    subtitle: 'Clock-ins, location data and adjustments',
    exportCsv: 'Export CSV',
    csvIn: 'Clock in',
    csvOut: 'Clock out',
    csvBreak: 'Break (min)',
    csvDistance: 'Distance (m)',
    csvReason: 'Reason',
    flaggedOnly: 'Flagged only',
    searchPlaceholder: 'Employee, reason, flag…',
    includeUnreviewed: 'Include unreviewed in the export',
    includeUnreviewedHint: 'Includes auto-closed and disputed shifts',
    countOne: '{count} shift',
    countMany: '{count} shifts',
    reviewableTotal: 'Reviewable total:',
    position: 'Location',
    duration: 'Duration',
    flags: 'Flags',
    onSite: 'On site',
    gpsUnavailable: 'No GPS',
    excludedOne:
      '{count} shift is excluded from the total and the export because it was auto-closed or disputed. It needs reviewing before it is paid.',
    excludedMany:
      '{count} shifts are excluded from the total and the export because they were auto-closed or disputed. They need reviewing before they are paid.',
  },

  assignments: {
    notesOptional: 'Notes (optional)',
    title: 'Site assignments',
    subtitle:
      'Who works where. An employee can only clock in at the site they are assigned to that day.',
    create: 'New assignment',
    createTitle: 'Assign a crew to a site',
    selectSite: 'Select…',
    jobOptional: 'Job (optional)',
    jobOptionalHint: 'Only needed to bill the hours to a job.',
    noJob: 'None',
    startsOn: 'From',
    endsOn: 'To',
    openEnded: 'Project assignment (no end date)',
    project: 'project',
    selectedCount: 'Employees ({count} selected)',
    notesPlaceholder: 'e.g. evening shift, access from the side road',
    assignOne: 'Assign {count} employee',
    assignMany: 'Assign {count} employees',
    assigning: 'Assigning…',
    createError: 'Could not create the assignment.',
    unassignedOne: '{count} employee with no site on {date}',
    unassignedMany: '{count} employees with no site on {date}',
    unassignedHint: 'Without an assignment the clock-in is recorded but flagged as an exception.',
    noneForDay: 'No assignments for {date}.',
    area: '{radius} m area',
    noJobShort: 'no job',
    remove: 'Remove',
    removeConfirm:
      'Remove {name} from {site}? Shifts already clocked against it are left untouched.',
  },

  payroll: {
    title: 'Hours and payroll',
    subtitle: 'Hours per employee and per site, exportable for the accountant',
    downloadPdf: 'Download PDF',
    downloadExcel: 'Download Excel',
    preparing: 'Preparing…',
    groupBy: 'Group by',
    byWorker: 'Employee',
    bySite: 'Site',
    includeUnreviewed: 'Include unreviewed shifts',
    totalHours: 'Total hours',
    decimalHoursHint: '{value} decimal hours',
    countedShifts: 'Shifts counted',
    excluded: 'Excluded',
    toVerify: 'To review',
    decimalHours: 'Decimal hours',
    noShifts: 'No shifts in the selected period.',
    exportFailed: 'Export failed.',
    detail: 'Detail',
    hide: 'Hide',
    autoClosed: 'Auto-closed',
    disputed: 'Disputed',
    outsideArea: 'Outside area',
    excludedOne:
      '{count} shift excluded from the total and the export because it was auto-closed or disputed. Review it under Shifts before paying it.',
    excludedMany:
      '{count} shifts excluded from the total and the export because they were auto-closed or disputed. Review them under Shifts before paying them.',
  },

  personal: {
    heading: 'Personal details',
    photo: 'Badge photo',
    uploadPhoto: 'Upload photo',
    changePhoto: 'Change photo',
    removePhoto: 'Remove',
    photoFailed: 'Could not upload the photo.',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    saveFailed: 'Could not save.',
    firstName: 'First name',
    lastName: 'Last name',
    jobTitle: 'Job title',
    phone: 'Phone',
    dateOfBirth: 'Date of birth',
    birthPlace: 'Place of birth',
    taxCode: 'Tax code',
    hiredOn: 'Hired on',
    badgeNumber: 'Badge number',
    address: 'Address',
    emergencyContact: 'Emergency contact',
  },

  employees: {
    assignmentRange: 'from {from} to {to}',
    assignmentOpen: 'from {from} — project-long',
    hoursRecent: 'Hours (last 30 shifts)',
    title: 'Employees',
    subtitle: 'App accounts are created here — there is no self-registration',
    create: 'New employee',
    createHint:
      'With an email they get an invitation with a temporary password. Without one they are only recorded; create the account later.',
    addOnly: 'Add employee',
    createAccount: 'Create account',
    firstName: 'First name',
    lastName: 'Last name',
    phone: 'Phone',
    searchPlaceholder: 'Name, email, role…',
    countOne: '{count} employee',
    countMany: '{count} employees',
    role: 'Role',
    createdAt: 'Created',
    email: 'Email',
    name: 'Name',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    backToList: 'Back to the list',
    hoursLast30: 'Hours (last 30 shifts)',
    shiftsRecorded: 'Shifts recorded',
    withFlags: 'Flagged',
    documentsToHandle: 'Documents to handle',
    ofExpected: 'of {total} expected',
    recentShifts: 'Recent shifts',
    noShifts: 'No shifts recorded.',
    activeAssignments: 'Active assignments',
    noAssignments: 'No site assigned.',
    assign: 'Assign',
    documentFile: 'Document file',
    manage: 'Manage',
    fromDate: 'from {date}',
    toDate: 'to {date}',
    onProject: '— for the project',
    privacy: 'Privacy',
    locationNotice: 'Location notice:',
    noticeRead: 'read on {date}',
    noticeUnread: 'not read yet',
    noticeExplainer:
      'An acknowledgement of having read it, not consent: the legal basis is the employment contract and legitimate interest.',
    onFile: 'on file',
    notOnFile: 'not on file',
    expiresOn: 'expires {date}',
  },

  sites: {
    clientHint: 'Who the work is for. Jobs on this site inherit it.',
    noClient: '— No client —',
    newClient: '+ New client…',
    newClientPlaceholder: 'Client name',
    deleteTitle: 'Delete this site?',
    deleteConfirm:
      '“{name}” goes to the recycle bin and can be restored for 30 days. Past shifts are untouched.',
    tolerances: 'Clock-in tolerances',
    tolerancesHint:
      'Leave blank to use the general settings. Fill these in only for very large sites — airports, data centres, logistics parks — where the pin cannot be near every working position.',
    edgeTolerance: 'Grace beyond the boundary (m)',
    edgeToleranceHint:
      'Metres of slack outside the area before a clock-in is flagged. Different from widening the radius: the radius says how big the site is, this says how much to forgive at its edge.',
    trustOverride: 'Trusted accuracy (m)',
    rejectOverride: 'Unusable accuracy (m)',
    overrideEmpty: 'General',
    allowOutsideOverride: 'Clocking in outside the area',
    useGlobal: 'Use the general setting',
    forceAllow: 'Always allowed (with a reason)',
    forceDeny: 'Never allowed',
    largeSiteWarning:
      'Large site: past about 500 m of radius it is better to draw the real boundary than to widen the circle, or the fence takes in the neighbours too.',
    collapse: 'Shrink the map',
    dropPin: 'Place the pin',
    dropPinActive: 'Click the map to place the pin.',
    movePinHint: 'Drag the pin to move it.',
    mapModifier: 'Hold Shift to pan or zoom the map.',
    expand: 'Expand the map',
    pinOutside: 'The pin must sit inside the area — move it or redraw.',
    pinLocked: 'The pin cannot leave the drawn area.',
    done: 'Done',
    drawBoundary: 'Draw the boundary',
    editBoundary: 'Edit the boundary',
    clearBoundary: 'Remove the boundary',
    finishBoundary: 'Save the boundary',
    undoPoint: 'Undo point',
    boundaryActive: 'Boundary drawn with {count} points — it replaces the radius.',
    radiusActive: 'No boundary drawn — the {count} m radius applies.',
    pointsPlaced: '{count} points. Click to add more.',
    drawHint: 'Click the map to mark out the area. {count} more points needed.',
    noCoordinates: 'No coordinates — this will not move the pin.',
    addressSearch: 'Search for the address',
    addressSearchHint: 'Type and pick from the list: it fills town, postcode and coordinates.',
    addressPlaceholder: 'e.g. Via Roma 42, Milano',
    mapLabel: 'Location and clock-in area',
    namePlaceholder: 'e.g. Residenza Aurora — substation',
    radiusLabel: 'Geofence radius (m)',
    newSite: 'New site',
    radiusHint: 'Between 25 and 5000. A flat and a warehouse are not the same size.',
    accessNotes: 'Access notes',
    accessNotesHint:
      'Gate codes, parking, who to ask for. It is the most useful field the app has on site.',
    title: 'Sites',
    subtitle: 'Coordinates and clock-in area',
    create: 'New site',
    edit: 'Edit site',
    geofenceWarning:
      'The coordinates and the radius decide the outcome of every future clock-in at this site. Every change is written to the activity log.',
    client: 'Client',
    address: 'Address',
    coordinates: 'Coordinates',
    area: 'Area',
    missing: 'Missing',
    radius: 'Geofence radius (m)',
    searchPlaceholder: 'Name, client, address…',
    countOne: '{count} site',
    countMany: '{count} sites',
  },

  jobs: {
    assignTo: 'Assign to',
    assignToHint: 'The job appears in their app and they get a notification.',
    saveAsDraft: 'Save as draft',
    saveAsDraftHint: 'A draft is not confirmed yet: it shows in the app but nobody can clock in.',
    siteArea: 'Site area: {radius} m',
    attachments: 'Attachments',
    attachmentsHint:
      'Drawings, contracts, risk assessments. Assigned engineers see them in the app.',
    attachedCount: '{count} file(s) to attach',
    workingHours: 'Clock-in rules',
    workingHoursHint:
      'These only apply when the job has a planned time. Leave them on the general setting unless this job needs something different.',
    earlyLabel: 'Clocking in early',
    lateLabel: 'Clocking out late',
    useGlobal: 'Use the general setting',
    allowIt: 'Allowed',
    blockIt: 'Blocked',
    noScheduleNote:
      'With no planned time these rules do not apply \u2014 there is no \u201cbefore\u201d or \u201cafter\u201d.',
    noTeamWarning: 'No engineer assigned — this job appears in nobody’s app.',
    signatureRequired: 'Signature required',
    changeStatus: 'Change status',
    finalState: 'This job is in a final state and cannot be changed.',
    description: 'Description',
    access: 'Access',
    siteNotes: 'Notes from site',
    scheduling: 'Scheduling',
    statusChangeFailed: 'The status could not be changed.',
    descriptionPlaceholder: 'e.g. Replace the main distribution board',
    title: 'Jobs',
    subtitle: 'Scheduled, in progress and closed work',
    reference: 'Reference',
    jobTitle: 'Title',
    urgent: 'Urgent',
    scheduledStart: 'Scheduled start',
    scheduledEnd: 'Scheduled end',
    createJob: 'Create job',
    shiftsOnJob: 'Shifts on this job',
    searchPlaceholder: 'Reference, title, client…',
    countOne: '{count} job',
    countMany: '{count} jobs',
    client: 'Client',
    priority: 'Priority',
    scheduled: 'Scheduled',
    team: 'Team',
  },

  documents: {
    changeVisibility: 'Visibility',
    changeVisibilityTitle: 'Who can see “{name}”',
    changeVisibilityHint:
      'Narrowing it takes effect immediately. A document somebody already has open stays open until they reload.',
    customCategory: 'Your own label',
    customCategoryHint:
      'Whatever you call it. Shows in the list in place of “Other”, and is searchable.',
    customCategoryPlaceholder: 'e.g. Insurance policy',
    visibilityHint: 'Who can see this document in the app.',
    uploadDone: 'File uploaded.',
    dropHere: 'Drop a file here, or click to choose one',
    accepted: 'PDF, P7M, JPG, PNG, HEIC, Word, Excel, OpenDocument · max 50 MB (25 MB for images)',
    uploading: 'Uploading…',
    uploadFailed: 'The upload failed.',
    typeRejected: 'That format is not accepted. Use PDF, P7M, JPG, PNG, HEIC, Word, Excel or OpenDocument.',
    tooLarge: 'That file is too large — the limit is {size}.',
    uploadFor: 'Upload a file for “{name}”',
    addDocument: 'Upload a document',
    chooseCategory: 'Category',
    title: 'Documents',
    subtitle: 'Contracts, drawings, certificates and material from site',
    searchPlaceholder: 'File name',
    category: 'Category',
    visibility: 'Visibility',
    size: 'Size',
    uploaded: 'Uploaded',
    file: 'File',
    countOne: '{count} document',
    countMany: '{count} documents',
    deleteConfirm: 'Permanently delete {name}? The action stays in the activity log.',
  },

  folders: {
    addFile: 'Add a file (e.g. the back)',
    addFileTitle: 'Add a file to {label}',
    addFileHint: 'It joins the document on file, with the same dates: the back of an ID card, a second page.',
    dropSides: 'Drag the file here, or front and back together, or click to choose',
    addAnotherSide: 'Add another file to this document (e.g. the back)',
    sidesTogether: 'These {count} files will be one document (e.g. front and back).',
    fileCount: '{count} files',
    showFiles: 'Show all files ({count})',
    hideFiles: 'Hide files',
    foldersHintVehicle: 'Service invoices, fines, accident reports, anything else. Click a folder to open it.',
    newFolderHintVehicle: 'The folder appears for every vehicle.',
    newDocumentHintVehicle:
      'Added to every vehicle\'s list, e.g. "Telepass" or "Fuel card". You will be reminded before it expires.',
    vehiclePlaceholder: 'e.g. Fuel card',
    mandatoryVehicleHint: 'Shown as missing for every vehicle in use that does not have it.',
    correct: 'Correct dates and details',
    correctTitle: 'Correct — {name}',
    correctHint: 'For fixing a mistake. If the document was actually renewed, use Renew instead.',
    replaceFile: 'Replace file',
    attachFile: 'Attach file',
    shortCode: 'File name code',
    shortCodeHint: 'Uploaded files are renamed automatically, e.g. {example}. Leave empty to use the name.',
    autoNameHint: 'The file is renamed automatically with the document, the name and the expiry date.',
    replaceTitle: 'Replace file — {name}',
    attachTitle: 'Attach file — {name}',
    replaceHint:
      'The document stays the same — its dates and history; only the file changes. The previous file is kept in the activity log.',
    editItem: 'Rename or change item',
    removeItem: 'Remove from list',
    removedTitle: 'Removed items ({count}) — show',
    restore: 'Restore',
    documentsTitle: 'Documents',
    documentsHint: 'One current copy each. Uploading again renews it; the old one is archived.',
    summary: '{present} of {required} required documents on file',
    expiredCount: '{count} expired',
    expiringCount: '{count} expiring soon',
    noDocuments: 'No documents in this list yet.',
    foldersTitle: 'Folders',
    foldersHint: 'As many files as you need — courses, nominations, anything else. Click a folder to open it.',
    foldersHintCompany: 'As many files as you need — policies, contracts, reports. Click a folder to open it.',
    noFolders: 'No folders yet.',
    newDocument: 'New document',
    addCompanyDocument: 'Add company document',
    newFolder: 'New folder',
    newSubfolder: 'New subfolder',
    upload: 'Upload',
    more: 'More actions',
    edit: 'Edit',
    rename: 'Rename',
    history: 'Show previous versions',
    hideHistory: 'Hide previous versions',
    current: 'current',
    notOnFile: 'Not on file',
    noScan: 'Recorded without a file',
    emptyShort: 'Empty',
    emptyFolder: 'This folder is empty.',
    countOne: '{count} file',
    countMany: '{count} files',
    subCountOne: '{count} subfolder',
    subCountMany: '{count} subfolders',
    dropInto: 'Drag files here to add them to “{name}”, or click to choose',
    deleteFolderConfirm: 'Delete the folder “{name}”? Only an empty folder can be deleted.',
    deleteDocumentConfirm:
      'Remove “{name}” from the list? Only possible while nothing is on file. You can restore it later.',
    uploadTitle: 'Upload — {label}',
    multipleExplainer: 'Added to the folder; nothing already there is replaced.',
    renewExplainer: 'The current copy is archived, not overwritten.',
    dropMany: 'Drag files here, or click to choose — several at once is fine',
    dropOne: 'Drag the file here, or click to choose it',
    addMore: 'Add more files',
    uploadN: 'Upload {count} files',
    uploadingProgress: 'Uploading {done} of {total}…',
    fileFailed: '“{name}” was not uploaded: {reason}',
    noFileHint: 'No file yet? Save the dates now and attach the scan later.',
    notes: 'Notes',
    editTitle: 'Edit — {name}',
    subfolderTitle: 'New subfolder in “{name}”',
    newFolderHintWorker: 'The folder appears in every employee’s file.',
    newFolderHintCompany: 'A place for any company paperwork.',
    newDocumentHintWorker:
      'Added to every employee’s list, e.g. “Driving licence C”. You will be reminded before it expires.',
    newDocumentHintCompany:
      'Name it, then attach the file and its deadline. You will be reminded before it expires.',
    createAndUpload: 'Create and upload',
    createFolder: 'Create folder',
    name: 'Name',
    description: 'Description',
    folderPlaceholder: 'e.g. Nominations',
    workerPlaceholder: 'e.g. Driving licence C',
    companyPlaceholder: 'e.g. Fire safety certificate',
    tracksExpiry: 'Has an expiry date',
    tracksExpiryHint: 'The date is asked for on upload, and an alert comes before it.',
    warnDays: 'Remind me this many days before',
    mandatory: 'Required',
    mandatoryWorkerHint: 'Shown as missing for every employee who does not have it.',
    mandatoryCompanyHint: 'Shown as missing until it is on file.',
  },

  viewer: {
    preview: 'Preview',
    openTab: 'Open in a new tab',
    previous: 'Previous',
    next: 'Next',
    noPreview: 'No preview for this format',
    noPreviewHint: 'Word, Excel, HEIC photos and signed .p7m files open in their own programs. Download it to look at it.',
    failed: 'The preview could not be loaded.',
  },

  send: {
    button: 'Send documents',
    buttonCount: 'Send documents ({count})',
    select: 'Select {name}',
    selectAll: 'Select everyone shown',
    clearSelection: 'Clear selection',
    title: 'Send documents',
    hint: 'Choose the people and the documents, then email them to the customer or download a ZIP.',
    who: 'Who',
    selectedCount: '{count} selected',
    which: 'Which documents',
    employeeDocuments: 'Employee documents',
    companyDocuments: 'Company documents',
    all: 'Select all',
    none: 'Clear',
    summaryOne: '1 document · {size}',
    summary: '{count} documents · {size}',
    showFiles: 'Show the list',
    missing: '{count} not on file:',
    tooBig: 'Too large for one email. Split it into two sends, or download the ZIP.',
    toWhom: 'Send to',
    recipients: 'Customer email',
    recipientsHint: 'Up to 5 addresses, separated by commas.',
    subject: 'Subject',
    message: 'Message',
    defaultSubject: 'Staff documents - HH Electrical Systems',
    defaultMessage:
      'Good morning,\nplease find attached the documents requested for site access.\nKind regards',
    copyToMe: 'Send me a copy',
    privacy:
      'These documents contain personal data: send only what the customer asked for. Every document sent is recorded in the activity log.',
    emailPending:
      'Sending email from the portal starts as soon as AWS approves the email service. Until then, download the ZIP and attach it to an email from your own mailbox.',
    sendEmail: 'Send email',
    sending: 'Sending…',
    downloadZip: 'Download ZIP',
    preparing: 'Preparing…',
    zipReady: 'ZIP ready: {count} documents, {size}. The download has started.',
    sent: 'Sent {count} documents to {to}.',
  },

  vehicles: {
    drivingTitle: 'Company vehicle',
    drivingNone: 'No vehicle in use.',
    title: 'Vehicles',
    subtitle: 'Company vehicles: who is driving them, and whether their papers are in order',
    add: 'Add vehicle',
    addHint: 'Only the plate is required. The rest can be filled in later.',
    editTitle: 'Edit {plate}',
    searchPlaceholder: 'Plate, model, driver…',
    countOne: '{count} vehicle',
    countMany: '{count} vehicles',
    empty: 'No vehicles yet.',
    vehicle: 'Vehicle',
    plate: 'Plate',
    name: 'Nickname',
    nameHint: 'How the office calls it, e.g. "White van".',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    fuel: 'Fuel',
    registeredOn: 'First registration',
    km: 'Km',
    kmNow: 'Km now',
    kmHint: 'From the dashboard. Optional, but it is what the service interval and fuel card need.',
    vin: 'Chassis number (VIN)',
    notes: 'Notes',
    holder: 'Driver',
    driver: 'Driver',
    nobody: 'Nobody',
    since: 'since {date}',
    dueBack: 'due back {date}',
    dueBackOn: 'Due back on',
    inUse: 'In use',
    documents: 'Documents',
    documentsTitle: 'Vehicle documents',
    nextExpiry: 'Next expiry',
    nextExpiryOn: 'next expiry {date}',
    docsOk: 'In order',
    docsExpired: '{count} expired',
    docsExpiring: '{count} expiring',
    docsMissing: '{count} missing',
    details: 'Details',
    historyTitle: 'Who has had it',
    noHistory: 'Nobody has driven it yet.',
    from: 'From',
    to: 'To',
    assign: 'Assign',
    handOver: 'Hand over',
    return: 'Return',
    assignTitle: 'Assign {plate}',
    handOverTitle: 'Hand {plate} over from {name}',
    handOverHint: 'Closes the current driver\'s use and opens the new one in one step.',
    returnTitle: '{plate} back from {name}',
    toGarage: 'It goes straight to the garage (maintenance)',
    assignNotesPlaceholder: 'e.g. Milan site, with fuel card',
    returnNotesPlaceholder: 'e.g. scratch on the rear bumper',
    deleteConfirm: 'Delete {plate}? Its history and documents are kept.',
    status: {
      available: 'Available',
      assigned: 'In use',
      maintenance: 'In maintenance',
      retired: 'Retired',
    },
    fuels: {
      petrol: 'Petrol',
      diesel: 'Diesel',
      electric: 'Electric',
      hybrid: 'Hybrid',
      lpg: 'LPG',
      methane: 'Methane',
    },
  },

  rename: {
    title: 'Rename file',
    name: 'New name',
    extensionKept: 'The {ext} extension stays, so the file still opens in the right program.',
    file: 'Rename file',
    short: 'Rename',
  },

  exempt: {
    notRequiredPerson: 'Not required for this person',
    notRequiredVehicle: 'Not required for this vehicle',
    requiredAgain: 'Required again',
    confirm:
      '"{name}" will no longer show as missing or raise warnings for them. You can make it required again at any time. Continue?',
  },

  sheet: {
    export: 'Export Excel',
    exporting: 'Preparing…',
    import: 'Import Excel',
    importTitle: 'Import employees from Excel',
    importSitesTitle: 'Import sites from Excel',
    importVehiclesTitle: 'Import vehicles from Excel',
    newSite: 'new site',
    newVehicle: 'new vehicle',
    importCompanyTitle: 'Import company documents from Excel',
    newDocument: 'new document',
    importHint:
      'Use the file from "Export Excel". You will see every change before anything is saved. Empty cells leave data as it is; "-" clears a value.',
    drop: 'Drag the edited Excel file here, or click to choose it',
    checking: 'Reading the file…',
    updates: '{count} to update',
    creates: '{count} new',
    unchanged: '{count} unchanged',
    errors: '{count} with errors (skipped)',
    done: 'Saved: {count} employees',
    apply: 'Apply {count} changes',
    another: 'Choose another file',
    row: 'row {row}',
    new: 'new employee',
    skipped: 'will be skipped',
    changed: '{count} changes',
    empty: '(empty)',
    nothing: 'Nothing to change: the file matches what is in the portal.',
  },

  bulk: {
    button: 'Upload folders',
    title: 'Upload folders',
    hint: 'Drop one folder per person, named after them (e.g. "Mario Rossi"). Each file is matched to its document by its name. You check everything before anything is uploaded.',
    drop: 'Drag the folders here',
    dropMore: 'Drag more folders here',
    dropExample: 'e.g. a folder "Mario Rossi" with "carta identità.pdf", "codice fiscale.pdf", "contratto.pdf"',
    pick: 'Choose a folder',
    pickNote: 'Chrome asks to confirm when you choose a folder; dragging it in does not.',
    pickFiles: 'Choose single files',
    photoNeedsImage: 'Photo (only JPG or PNG images)',
    looseFiles: 'Files without a folder',
    choosePerson: 'Choose the person',
    person: 'Person for the folder {folder}',
    noMatch: 'Not found: choose who it belongs to',
    matched: 'Found from the folder name',
    chosen: 'Chosen by hand',
    removeFolder: 'Remove',
    replaces: 'replaces the one on file (expires {date})',
    replacesUndated: 'replaces the one on file',
    typeFor: 'Document for {name}',
    skip: '- Do not upload -',
    expiryFor: 'Expiry date for {name}',
    expiry: 'Expiry date',
    photo: 'Photo (profile and identification card)',
    sameDates: 'Same dates as the first file',
    upload: 'Upload {count} files',
    uploading: 'Uploading {done} of {total}…',
    needPerson: '{count} folders without a person',
    needDates: '{count} expiry dates missing',
    done: '{count} files uploaded',
    failedCount: '{count} failed: see the reason, then try again',
    notAccepted: 'This type of file is not accepted.',
    tooBig: 'Too big ({size}).',
  },

  badge: {
    title: 'Identification card',
    needs: 'To make it, add: {fields}.',
    ready: 'Ready to print, with the photo on file.',
    readyNoPhoto: 'Ready: choose the photo when you generate it.',
    generate: 'Generate card',
    dialogTitle: 'Identification card - {name}',
    hint: 'Check the photo, then generate the card: a PDF to print, cut out and laminate.',
    photoProfile: 'The profile photo',
    photoFound: 'Found among the documents: {name}',
    photoChosen: 'The photo you chose',
    noPhoto: 'No photo found: choose one from your computer.',
    unusable: 'The profile photo is in a format the card cannot use: choose another one.',
    choose: 'Choose another photo',
    done: 'Done: generate the card',
    generating: 'Generating…',
    downloaded: 'Card generated: {name}',
    missing: 'Still missing: {fields}. Complete the personal details first.',
  },

  renameAll: {
    button: 'Rename all files',
    title: 'Rename all files',
    hint: 'Gives every file already on file the automatic name: code, name, expiry date. New uploads are already named this way. The old names stay in the activity log.',
    count: '{count} files will be renamed. For example:',
    more: 'and {count} more',
    apply: 'Rename {count} files',
    nothing: 'All files already have the automatic name.',
    done: 'Done: {count} files renamed.',
  },

  compliance: {
    title: 'Documents and expiries',
    subtitle: 'Employee and company documents, with the renewals that are due',
    missingDocuments: 'Missing documents',
    expired: 'Expired',
    expiring: 'Expiring',
    companyDocuments: 'Company documents',
    toHandle: '{count} to handle',
    toHandleShort: 'To handle',
    completeness: 'Completeness',
    missing: 'Missing',
    openFile: 'Open file',
    closeFile: 'Close',
    fileFor: 'File — {name}',
    fileSummary: '{missing} missing · {expired} expired · {expiring} expiring',
    document: 'Document',
    reference: 'Reference',
    issued: 'Issued',
    expiry: 'Expiry',
    register: 'Record',
    renew: 'Renew',
    recordTitle: 'Record — {label}',
    renewTitle: 'Renew — {label}',
    recordExplainer:
      'The previous document is archived, not overwritten: "what did we have on file on 12 March" is a question that eventually gets asked.',
    filename: 'File name',
    referenceNumber: 'Number / protocol',
    issuedAt: 'Issue date',
    expiryRequired: 'Required for this document.',
    mandatory: 'Mandatory',
  },

  alerts: {
    title: 'Expiries and alerts',
    subtitle: 'Generated automatically each night from documents and certifications',
    rescan: 'Re-check now',
    rescanning: 'Checking…',
    critical: 'Critical',
    criticalHint: 'expired or within 14 days',
    warning: 'Warning',
    warningHint: 'within 30 days, or missing',
    info: 'Informational',
    showAcknowledged: 'Also show the ones already picked up',
    countOne: '{count} alert',
    countMany: '{count} alerts',
    severity: 'Severity',
    searchPlaceholder: 'Employee, document…',
    alert: 'Alert',
    concerns: 'Concerns',
    kind: 'Type',
    dueOn: 'Due',
    detectedAt: 'Detected',
    acknowledge: 'Pick this up',
    acknowledged: 'picked up',
    acknowledgedBy: 'Picked up by {name} — {note}',
    acknowledgeExplainer:
      'The alert stays open until the document is renewed, but stops appearing among the ones to handle.',
    notePlaceholder: 'e.g. renewal requested on 21/09, expected by Thursday',
    noneOpen: 'No open expiries. Every document is in order.',
    noneToHandle: 'Nothing to handle.',
  },

  calendar: {
    title: 'Calendar',
    subtitle: 'Appointments, deadlines and absences in one place.',
    today: 'Today',
    previous: 'Previous month',
    next: 'Next month',
    add: 'New appointment',
    addForDay: 'Add',
    allDay: 'All day',
    until: 'until {date}',
    nothing: 'Nothing scheduled on this day.',
    more: '+{count}',
    itemsOne: '{count} item',
    itemsMany: '{count} items',
    kind: {
      appointment: 'Appointment',
      deadline: 'Deadline',
      leave: 'Absence',
    },
    source: {
      document: 'Document',
      job: 'Job ends',
      equipment: 'Equipment due back',
      vehicle: 'Vehicle due back',
      approved: 'Approved',
      pending: 'Awaiting approval',
    },
    attendees: 'Attendees',
    everyone: 'Visible to everyone',
    open: 'Open',
    editTitle: 'Edit appointment',
    newTitle: 'New appointment',
    fieldTitle: 'Title',
    place: 'Place',
    startDate: 'Start',
    endDate: 'End (optional)',
    startTime: 'Start time',
    endTime: 'End time (optional)',
    site: 'Site',
    job: 'Job',
    noSite: 'No site',
    noJob: 'No job',
    attendeesField: 'Who is coming',
    attendeesHint: 'If you pick nobody, the appointment is visible to everyone.',
    attendeesSelf: 'Only you and the office can see it.',
    endBeforeStart: 'The end is before the start.',
    deleteConfirm: 'Delete the appointment {title}?',
    deleteConfirmLabel: 'Delete appointment',
    legend: 'Key',
  },

  leave: {
    title: 'Leave and permits',
    subtitle: "Employees' requests: the office approves or rejects them.",
    tabs: {
      pending: 'To handle',
      approved: 'Approved',
      rejected: 'Rejected',
      all: 'All',
    },
    status: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    },
    person: 'Employee',
    type: 'Type',
    period: 'Period',
    note: "Employee's note",
    decision: 'Decision',
    requested: 'Requested on',
    hoursRange: 'from {from} to {to}',
    approve: 'Approve',
    reject: 'Reject',
    searchPlaceholder: 'Employee, type, notes…',
    countOne: '{count} request',
    countMany: '{count} requests',
    empty: 'No requests in this view.',
    cannotDecide: 'You cannot approve or reject: by setting, only the administrator decides requests.',
    officeOnly: 'This page is for the office. Leave and permit requests are sent from the app.',
    disabled: 'Requests from the app are switched off in Settings. Those already sent stay here.',
    decidedBy: 'by {name}',
    approveTitle: 'Approve the request',
    rejectTitle: 'Reject the request',
    dialogHint: 'The employee gets a notification with your answer.',
    noteLabel: 'Note for the employee (optional)',
    notePlaceholder: 'E.g. confirmed, or: too many colleagues away on those days',
  },

  audit: {
    adminOnly: 'Administrators only',
    adminOnlyHint:
      'The activity log records what everyone in the office does, managers included, so only an administrator can read it — that is what makes it evidence. Ask whoever holds the administrator account.',
    title: 'Activity log',
    subtitle: 'Append-only. Nothing can be deleted from here.',
    actor: 'User',
    action: 'Action',
    entity: 'Object',
    when: 'When',
    immutableNotice:
      'This log cannot be edited or deleted. It is the evidence that protects both the company and the employee when a shift is disputed.',
    system: 'System',
    searchPlaceholder: 'User, action, object…',
    countOne: '{count} entry',
    countMany: '{count} entries',
    actions: {
      user_created: 'User created',
      user_updated: 'User updated',
      user_exported: 'Employees exported to Excel',
      user_reinvited: 'Invitation resent',
      user_login_created: 'Name login created',
      user_password_reset: 'Password reset',
      user_suspended: 'User suspended',
      user_reactivated: 'User reactivated',
      shift_clock_in: 'Clock in',
      shift_clock_out: 'Clock out',
      shift_adjusted: 'Shift adjusted',
      shift_auto_closed: 'Shift auto-closed',
      job_created: 'Job created',
      job_status_changed: 'Job status changed',
      job_assigned: 'Job assigned',
      job_unassigned: 'Assignment removed',
      document_type_created: 'Folder created',
      document_type_updated: 'Folder changed',
      document_type_deleted: 'Folder deleted',
      document_uploaded: 'Document uploaded',
      vehicle_created: 'Vehicle added',
      vehicle_updated: 'Vehicle changed',
      vehicle_assigned: 'Vehicle assigned',
      vehicle_returned: 'Vehicle returned',
      vehicle_deleted: 'Vehicle deleted',
      document_exempted: 'Document marked not required',
      document_exemption_removed: 'Document required again',
      document_sent: 'Document sent by email',
      document_bulk_renamed: 'Files renamed automatically',
      document_viewed: 'Document previewed',
      document_downloaded: 'Document downloaded',
      document_deleted: 'Document deleted',
      site_created: 'Site created',
      site_updated: 'Site updated',
      settings_changed: 'Settings changed',
      assignment_created: 'Assignment created',
      assignment_removed: 'Assignment removed',
      alert_acknowledged: 'Alert picked up',
      document_recorded: 'Document recorded',
      calendar_event_created: 'Appointment created',
      calendar_event_updated: 'Appointment changed',
      calendar_event_deleted: 'Appointment deleted',
      leave_requested: 'Leave or permit requested',
      leave_cancelled: 'Request cancelled',
      leave_approved: 'Request approved',
      leave_rejected: 'Request rejected',
    },
  },

  inventory: {
    descriptionPlaceholder: 'What it is, and anything worth knowing about this one.',
    purchasePriceHint: 'Only the office sees this.',
    moreDetails: 'More details',
    moreDetailsHint: 'Category, make, purchase',
    delete: 'Delete item',
    deleteTitle: 'Delete this item?',
    deleteConfirm:
      '{code} \u2014 {name} goes to the recycle bin and can be restored for 30 days.',
    deleteOutWarning:
      'This item is signed out to {holder}. Record the return first \u2014 deleting it now would lose the only record of who has it.',
    statusHint_available: 'In the store, ready to hand out.',
    statusHint_assigned: 'Out with somebody. Set by handing it over, not by hand.',
    statusHint_maintenance: 'Back, but not usable \u2014 at the repair shop, away for calibration, waiting for a part. It stays in the register and out of the hand-out list.',
    statusHint_lost: 'Not coming back, as far as anyone knows.',
    statusHint_retired: 'Scrapped or sold. Kept for the history, not for use.',
    statusMeaning: 'What the statuses mean',
    scan: 'Scan',
    scanTitle: 'Scan a label',
    scanHint: 'Point the camera at the QR code or barcode on the tool.',
    scanStart: 'Start the camera',
    scanStop: 'Stop',
    scanAgain: 'Scan another',
    scanNoCamera: 'No camera available',
    scanNoCameraHint:
      'This device has no camera, or the browser will not give access to it. You can still type the code in.',
    scanDenied: 'Camera blocked',
    scanDeniedHint:
      'Allow camera access for this site in your browser settings, then try again.',
    scanInsecure: 'Camera needs a secure connection',
    scanInsecureHint:
      'Browsers only give camera access over HTTPS. This will work once the portal is deployed; on this machine it works at localhost.',
    scanManual: 'Or type the code',
    scanManualAction: 'Find it',
    scanLooking: 'Looking\u2026',
    scanFound: 'Found',
    handOver: 'Hand over',
    handBack: 'Take back',
    scanReady: 'In the store',
    scanReadyHint: 'Nobody has this. Hand it to somebody below.',
    scanOut: 'Out with {holder}',
    scanOutHint: 'Since {date}. Take it back below.',
    installHint:
      'Add the portal to your home screen and the scanner opens in one tap, with no address bar in the way.',
    category_power_tools: 'Power tools',
    category_measuring: 'Measuring instruments',
    category_ppe: 'PPE',
    category_access: 'Ladders and towers',
    category_vehicles: 'Vehicles',
    category_other: 'Other',
    allCategories: 'All categories',
    allStatuses: 'All statuses',
    photoOf: 'Photo of {name}',
    noPhoto: 'No photo',
    selectRow: 'Select {name}',
    selectAll: 'Select everything shown',
    selectedOne: '{count} selected',
    selectedMany: '{count} selected',
    clearSelection: 'Clear selection',
    printSelected: 'Print {count} labels',
    printAll: 'Print all labels',
    printSheetSelected: 'Labels for the {count} items you selected.',
    printSheetAll: 'Labels for every item in the inventory.',
    printSaveHint:
      'Your browser’s print dialog can save this as a PDF instead of printing it — choose “Save as PDF” as the destination.',
    sectionItem: 'What it is',
    sectionItemHint: 'The name is what people will search for.',
    sectionPurchase: 'Purchase',
    sectionPurchaseHint: 'Optional, and only the office sees it.',
    sectionLabel: 'Label',
    sectionPhoto: 'Photo',
    removePhoto: 'Remove photo',
    resolving: 'Looking up the item…',
    scanFailed: 'Item not found',
    scanFailedHint: 'No item matches the code “{code}”.',
    title: 'Inventory',
    subtitle: 'Tools and equipment, and who has them right now.',
    searchPlaceholder: 'Code, name, serial, who has it\u2026',
    countOne: '{count} item',
    countMany: '{count} items',
    addItem: 'Add an item',
    addItemHint:
      'The code is generated for you and goes on the label. The manufacturer\u2019s serial is a separate field.',
    item: 'Item',
    code: 'Code',
    category: 'Category',
    serialNumber: 'Serial number',
    serialHint: 'The manufacturer\u2019s \u2014 it is the number an insurer or a police report wants.',
    manufacturer: 'Manufacturer',
    model: 'Model',
    purchasedOn: 'Purchased on',
    purchasePrice: 'Purchase price',
    description: 'Description',
    namePlaceholder: 'e.g. Hilti TE 30 drill',
    photo: 'Photo',
    photoHint: 'Useful for recognising it when it comes back.',
    photoLabel: 'Inventory photo',

    holder: 'Held by',
    holderPerson: 'Employee',
    holderSite: 'Site',
    inStore: 'In the store',
    dueBack: 'Due back',
    dueBackHint: 'Leave blank if there is no return date.',
    checkedOutAt: 'Handed over',
    overdue: 'Overdue',
    overdueBy: '{count} days late',
    overdueBanner: '{count} items are past their return date. They are at the top of the list.',

    status_available: 'Available',
    status_assigned: 'Assigned',
    status_maintenance: 'In maintenance',
    status_lost: 'Lost',
    status_retired: 'Retired',

    checkOut: 'Hand over',
    checkOutTitle: 'Hand over \u201c{name}\u201d',
    checkOutHint: 'It stays assigned until you record the return.',
    checkIn: 'Record return',
    checkInTitle: 'Return of \u201c{name}\u201d',
    giveTo: 'To whom',
    giveToHint: 'An employee, or a site if the tool stays there.',
    conditionOut: 'Handover notes',
    conditionHint: 'Condition, what is included, what has to come back.',
    conditionIn: 'Return notes',
    backAs: 'Status on return',
    backAsHint: 'Mark it \u201cIn maintenance\u201d if it came back to be repaired.',

    history: 'Movements',
    historyHint:
      'Every handover and every return. These rows are never edited \u2014 they are the answer to \u201cwho had it in March\u201d.',
    neverIssued: 'This item has never been handed out.',
    stillOut: 'Still out',
    outOn: 'Out on {date}',
    backOn: 'back on {date}',
    backToList: 'Back to inventory',

    label: 'Label',
    labelHint:
      'Print it and stick it on the tool. Pointing a camera at the code opens this page.',
    labelKind: 'Label type',
    labelKindHint: 'Choose barcode if the tool already carries one.',
    qr: 'QR code',
    barcode: 'Barcode',
    externalCode: 'Existing code',
    externalCodeHint: 'If the tool already has a manufacturer label, scanning it will still find this item.',
    print: 'Print',
    printLabels: 'Print labels',
    printLabelsHint: 'One sheet with every label, to cut out and stick on.',
  },

  recycle: {
    inventory: 'Item',
    title: 'Recycle bin',
    subtitle:
      'Everything that has been deleted. Recoverable for {count} days, then removed for good.',
    subtitleShort: 'Deleted items, recoverable for a limited time.',
    searchPlaceholder: 'Name, type, who deleted it…',
    item: 'Item',
    kind: 'Type',
    client: 'Client',
    document: 'Document',
    deletedAt: 'Deleted',
    daysLeft: 'Days left',
    daysLeftValue: '{count} d',
    restore: 'Restore',
    purge: 'Delete permanently',
    purgeTitle: 'Delete permanently?',
    purgeConfirm: '“{name}” will be removed now and cannot be recovered.',
    countOne: '{count} item',
    countMany: '{count} items',
  },

  settings: {
    courses: {
      title: 'Courses and licences',
      hide: 'Hide course and licence documents from employees',
      hideHint:
        'Employees cannot open or download documents in the course and licence folders from the app. The office always sees them.',
    },
    calLeave: {
      title: 'Calendar and leave',
      hint: 'What employees see and can do from the app.',
      calendarInApp: 'Show the calendar in the app',
      calendarInAppHint: "Appointments, deadlines and absences appear in the employees' app.",
      employeesCanAdd: 'Employees may add appointments',
      employeesCanAddHint: 'An appointment an employee adds is seen only by them and the office.',
      leaveEnabled: 'Leave and permit requests are on',
      leaveEnabledHint: 'Employees can ask for leave and permits from the app. Requests already sent stay visible.',
      managersCanApprove: 'Managers may approve',
      managersCanApproveHint: 'When off, only the administrator approves or rejects requests.',
      typesTitle: 'Request types',
      typesHint: 'What employees can ask for. The name can be changed at any time; the internal code stays the one given at creation.',
      typeIt: 'Name in Italian',
      typeEn: 'Name in English',
      typeHours: 'In hours',
      typeHoursHint: 'One day only, with a start and an end time.',
      typeAdd: 'Add a type',
      typeRemove: 'Remove the type',
      typeKeepOne: 'At least one type must remain.',
      typeNameRequired: 'Every type needs a name in Italian.',
      typeRemovedNote: 'Requests already sent with a removed type stay in the list, showing the code instead of the name.',
      newType: 'New type',
    },
    auditNote: 'Every change anyone makes is recorded and cannot be edited or deleted. Administrators can read it here:',
    allowEarly: 'Allow clocking in before the planned start',
    allowEarlyHint:
      'Recommended. Arriving early and setting up is normal on site, and an early clock-in is flagged to the office either way.',
    allowLate: 'Allow clocking out after the planned end',
    allowLateHint:
      'Turn it off and somebody finishing late cannot clock out at all \u2014 their shift is auto-closed overnight instead, which is worse than a flag. This is the one that inflates an invoice, so reading the flags beats blocking.',
    graceLabel: 'Grace on the planned time (minutes)',
    graceHint:
      'Within this many minutes a clock-in counts as on time. With no grace, somebody clocking in at 08:57 for a 09:00 job is flagged every morning.',
    workingHours: 'Working hours',
    perJobNote: 'An individual job can carry its own rules \u2014 they are on the job itself.',
    theme: 'Theme',
    language: 'Language',
    perSiteNote:
      'These apply to every site. An individual site can carry its own tolerances — they are on the site itself, under the map.',
    openSites: 'Go to sites',
    location: 'Location',
    companyTitle: 'Company details on the identification card',
    companyHint: 'Printed at the top of every identification card. Change them here if they change.',
    companyName: 'Company name',
    companyVat: 'Tax code / VAT number',
    companyAddress: 'Registered address',
    companyEmployer: 'Employer (legal representative)',
    saveSettings: 'Save settings',
    radiusLabel: 'Default geofence radius (m)',
    radiusHint: 'Used for new sites. Each site can override it.',
    accuracyTrustLabel: 'Trusted accuracy (m)',
    accuracyTrustHint: 'Below this figure the GPS fix is treated as reliable.',
    accuracyRejectLabel: 'Unusable accuracy (m)',
    accuracyRejectHint:
      'Above this figure the fix counts as “no GPS” rather than “outside the area”.',
    maxShiftLabel: 'Auto-close after (hours)',
    maxShiftHint: 'A shift left open past this limit is closed and flagged for review.',
    offlineAgeLabel: 'Maximum age of offline clock-ins (hours)',
    offlineAgeHint: 'Past this limit a clock-in needs manual review. It is never discarded.',

    locationNote:
      'A location is recorded only at clock-in and clock-out, never during the shift.',
    title: 'Settings',
    subtitle: 'Your account, company rules, and who can use the portal',
    appearance: 'Appearance',
    appearanceHint: 'Applies to this browser only.',
    clockIn: 'Clock-in',
    allowOutside: 'Allow clocking in outside the area (a reason is required)',
    allowOutsideHint:
      'Recommended. Turn it off and an employee who cannot clock in cannot start work — and the office ends up correcting shifts by hand, which is worse for everyone than an honest flag.',
    saved: 'Settings saved.',
  },

  alertText: {
    withHolder: '{holder} — {label}',
    expired: '{subject} expired',
    expiring: '{subject} expiring',
    expiresToday: '{subject} expires today',
    missing: '{subject} missing',
    missingBody: 'A mandatory document that is not on file.',
    lastDay: 'Last day it is valid.',
    expiredYesterday: 'It expired yesterday. It must be renewed before the next site visit.',
    expiredDaysAgo:
      'It expired {count} days ago. It must be renewed before the next site visit.',
    expiresTomorrow: 'Expires tomorrow.',
    expiresInDays: 'Expires in {count} days. Worth starting the renewal now.',
  },

  status: {
    job: {
      draft: 'Draft',
      scheduled: 'Scheduled',
      in_progress: 'In progress',
      on_hold: 'On hold',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    shift: {
      open: 'In progress',
      closed: 'Closed',
      auto_closed: 'Auto-closed',
      disputed: 'Disputed',
    },
    user: {
      invited: 'Invited',
      active: 'Active',
      suspended: 'Suspended',
      archived: 'Archived',
    },
    role: {
      admin: 'Administrator',
      manager: 'Employee',
      engineer: 'Employee',
    },
    register: {
      valid: 'Valid',
      expiring: 'Expiring',
      expired: 'Expired',
      missing: 'Missing',
      not_provided: 'Not provided',
      exempt: 'Not required',
    },
    severity: {
      critical: 'Critical',
      warning: 'Warning',
      info: 'Informational',
    },
    docCategory: {
      contract: 'Contract',
      drawing: 'Drawing',
      certificate: 'Certificate',
      risk_assessment: 'Risk assessment',
      datasheet: 'Datasheet',
      site_photo: 'Site photo',
      signed_sheet: 'Signed sheet',
      signature: 'Signature',
      id_document: 'ID document',
      other: 'Other',
    },
    flag: {
      outside_geofence: 'Outside area',
      mocked_location: 'Mocked location',
      poor_accuracy: 'Low accuracy',
      no_fix: 'No GPS',
      offline_sync: 'Sent offline',
      auto_closed: 'Auto-closed',
      manually_adjusted: 'Manually adjusted',
      no_job_assigned: 'No job',
      overlapping_shift: 'Overlapping shifts',
    },
    alertKind: {
      document_expiring: 'Document expiring',
      document_expired: 'Document expired',
      document_missing: 'Document missing',
      certification_expiring: 'Certification expiring',
      certification_expired: 'Certification expired',
      shift_exception: 'Shift exception',
      unassigned_worker: 'Unassigned employee',
    },
  },

  auth: {
    title: 'Sign in',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signOut: 'Sign out',
    forgot: 'Forgot your password?',
    back: 'Go back',
    showPassword: 'Show password',
    hidePassword: 'Hide password',

    mfa: {
      title: 'Two-step verification',
      subtitle: 'Enter the 6-digit code from your authenticator app.',
      subtitleEmail: 'We sent a 6-digit code to your email.',
      code: 'Verification code',
      verify: 'Verify',
      resend: 'Send again',
      resent: 'Code sent again.',
    },

    setupMfa: {
      title: 'Turn on two-step verification',
      subtitle:
        'Scan this code with Google Authenticator, Microsoft Authenticator or 1Password, then enter the 6-digit code it shows.',
      manual: 'Or enter this key by hand:',
      copied: 'Key copied.',
      confirm: 'Turn on',
      required: 'Your role requires two-step verification.',
      skip: 'Skip for now',
    },

    newPassword: {
      title: 'Choose a new password',
      subtitle: 'This is your first sign-in. The temporary password stops working after this step.',
      password: 'New password',
      confirm: 'Confirm password',
      submit: 'Save and sign in',
      mismatch: 'The two passwords do not match.',
      rules: 'At least 12 characters, with an uppercase letter, a lowercase letter and a number.',
    },

    forgotFlow: {
      invitationSent: 'You have not set a password yet, so we have sent you a new invitation with a temporary password. Check your email.',
      title: 'Reset your password',
      subtitle: 'Enter your email. If an account exists, you will receive a 6-digit code.',
      send: 'Send code',
      sent: 'If that address is registered, the code is on its way.',
      codeTitle: 'Enter the code',
      codeSubtitle: 'Check your email, then choose a new password.',
      code: 'Code',
      newPassword: 'New password',
      confirm: 'Confirm password',
      submit: 'Reset password',
      done: 'Password updated. You can sign in now.',
    },

    remember: {
      label: 'Remember me on this device',
      welcomeBack: 'Welcome back',
      notYou: 'Not you? Use another account',
      orPassword: 'or with your password',
    },
    passkey: {
      signIn: 'Sign in with a passkey',
      or: 'or',
      needEmail: 'Enter your email first, then use your passkey.',
    },

    secure: {
      title: 'Secure your account',
      subtitle: 'Add a second layer of security. You can also do this later from Settings → Account.',
      passkey: 'Add a passkey',
      passkeyHint: 'Sign in with your fingerprint, face or device PIN. No codes to type.',
      recommended: 'Recommended',
      app: 'Authenticator app',
      appHint: 'A 6-digit code from Google Authenticator, Microsoft Authenticator or 1Password.',
      skip: 'Skip for now',
    },

    session: {
      expiring: 'Your session is about to end',
      expiringBody: 'You will be signed out in {seconds} seconds due to inactivity.',
      stay: 'Stay signed in',
      timedOut: 'Session ended after inactivity. Please sign in again.',
      maxReached: 'Session ended. For safety, a sign-in lasts at most 5 hours.',
      noPortalAccess: 'This account has no access to the internal portal. Ask an administrator to enable it.',
    },

    error: {
      invalid: 'Incorrect email or password.',
      code: 'That code is wrong or has expired.',
      attempts: 'Too many attempts. Try again in a few minutes.',
      disabled: 'This account is not active. Contact the office.',
      password: 'That password does not meet the minimum requirements.',
      network: 'Could not reach the server. Check your connection.',
      unknown: 'Something went wrong. Please try again.',
      noPasskey: 'Passkey sign-in is not available for this account: no passkey is registered, or the authenticator app is on (the two do not work together). Sign in with your password.',
      passkeyCancelled: 'The passkey was cancelled or is not on this device.',
      passkeyUnsupported: 'This browser or device cannot use passkeys for this portal.',
      passkeyExists: 'This device already has a passkey for your account.',
      notConfigured: 'Authentication is not configured for this build. Contact your administrator.',
    },
  },

  access: {
    noEmail: 'No email — no account yet',
    noEmailHint:
      'Without an email the person is added without an account: you can file their documents straight away. After adding them you can create an app sign-in with first name, surname and a temporary password, or add the email later from their page.',
    noAccountYet:
      'No account yet. Add their email and choose where they will sign in; they will receive the invitation by email.',
    createAccount: 'Create account',
    createAccountHint: 'An invitation is sent to this address.',
    invited: 'Invited',
    noSignIn: 'No sign-in',
    delete: 'Delete',
    deleting: 'Deleting…',
    deleteConfirm: 'Delete {name} ({email})?\n\nThey will no longer be able to sign in and will disappear from every list. Their shifts, documents and the activity log stay: those are records the company must keep.',
    reinvite: 'Resend invitation',
    reinviting: 'Sending…',
    reinviteDone: 'Invitation sent',
    reinviteSent: 'A new temporary password was sent to {email}.',
    reinviteHint: 'Sends a new temporary password: the one in the first invitation expires after 7 days.',
    nameLogin: {
      heading: 'Sign in to the app by name',
      loginName: 'Login name',
      none: 'No name login yet. Create one so they can open the app with their first name, surname and a temporary password.',
      typedHint: 'In the app they type first name "{first}", surname "{last}" and their password.',
      differentName:
        'This login name is not the same as first name and surname: they must type exactly "{loginName}".',
      create: 'Create name login',
      reset: 'Reset password',
      createTitle: 'Name login for {name}',
      resetTitle: 'Reset the password of {name}',
      resultTitle: 'Temporary password for {name}',
      createIntro:
        'Creates the app sign-in with first name and surname and a temporary password. At first sign-in they will have to choose their own password.',
      resetIntro:
        'Generates a new temporary password. The old one stops working at once and at the next sign-in they will have to choose their own.',
      leavesEmail:
        'Today they sign in with the email {email}. With a name login they will sign in with first name and surname, and the old password will stop working.',
      offerAfterAdd:
        '{name} was added without an email. Create the app sign-in with first name and surname now? You will get a temporary password to hand over.',
      notNow: 'Not now',
      generate: 'Generate password',
      needName:
        "A login name cannot be worked out from this person's name. Type one: lowercase letters, digits and dots (for example mario.rossi).",
      nameInvalid: 'Use only lowercase letters without accents, digits and dots, for example mario.rossi.',
      password: 'Temporary password',
      copy: 'Copy',
      copied: 'Copied',
      copyFailed: 'Copy failed: select the password and copy it by hand.',
      onceWarning:
        'This password is shown only now: it is not stored and cannot be shown again. At first sign-in the person will have to choose their own password. If you lose it, reset it.',
      instructionLabel: 'Instructions to hand over',
      instruction:
        'Open the HH Electrical Systems app. Sign in with: {first} {last}, password: {password}.',
      instructionLogin:
        'Open the HH Electrical Systems app. Sign in with the login name: {loginName}, password: {password}.',
      copyInstruction: 'Copy instructions',
      done: 'Done',
    },
    heading: 'Access and permissions',
    signsInTo: 'Can sign in to',
    manage: 'Manage access',
    discard: 'Discard changes',
    portalAccess: 'What they can do in the portal',
    permissionsForNew: 'What they will be able to do in the portal',
    summary: '{visible} sections visible · {editable} editable',
    fullAccess: 'Administrators have full access to every section.',
    appOnly: 'App only — no portal access. Turn on “Internal portal” above to choose what they can see.',
    setAll: 'All:',
    presets: {
      title: 'Start from:',
      viewAll: 'Sees everything',
      editDaily: 'Runs the day-to-day',
      nothing: 'No access',
    },
    groups: {
      overview: 'Overview',
      operations: 'Operations',
      records: 'Records',
      compliance: 'Compliance and messages',
      system: 'System',
    },
    tabGeneral: 'General',
    tabUsers: 'Users and permissions',
    title: 'Portal users',
    hint: 'Everyone has a role, which sets their starting permissions. Here you can change, section by section, what each person can see or edit.',
    newUser: 'New user',
    newUserHint:
      'With an email they get an invitation with a temporary password. Without one they are only recorded; create the account later.',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    role: 'Role',
    create: 'Create user',
    phone: 'Phone',
    kind: 'Who they are',
    kindAdmin: 'Administrator',
    kindAdminHint: 'Manages everything, including users and permissions. Always has the portal.',
    kindEmployee: 'Employee',
    kindEmployeeHint: 'Field engineer or office staff. What they can see is set in permissions.',
    doors: 'Accounts to create',
    doorPortal: 'Internal portal',
    doorPortalHint: 'Signs in on a computer: shifts, sites, documents, as permissions allow.',
    doorApp: 'App',
    doorAppHint: 'Clocks in, sees their own jobs and documents from the phone.',
    doorPortalShort: 'Portal',
    doorAppShort: 'App',
    noDoorsHint: 'No access: the person is recorded but cannot sign in until you open a door here.',
    created: 'User created. The email with the temporary password is on its way.',
    permissions: 'Permissions',
    section: 'Section',
    levelShort: {
      none: 'None',
      read: 'View',
      write: 'Edit',
    },
    level: {
      none: 'No access',
      read: 'View only',
      write: 'View & edit',
    },
    inherited: 'from role',
    save: 'Save permissions',
    saved: 'Permissions saved.',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    you: 'you',
    selfLock: 'You cannot remove your own ability to manage users.',
    empty: 'No users yet. Create the first one.',
    sections: {
      dashboard: 'Dashboard',
      shifts: 'Shifts',
      payroll: 'Hours and pay',
      assignments: 'Assignments',
      jobs: 'Jobs',
      employees: 'Employees',
      sites: 'Sites',
      documents: 'Documents',
      compliance: 'Expiries and required documents',
      alerts: 'Alerts',
      inventory: 'Inventory',
      vehicles: 'Vehicles',
      audit: 'Activity log',
      recycle_bin: 'Recycle bin',
      settings: 'Settings',
      users: 'Users and permissions',
      messages: 'Messages',
      calendar: 'Calendar',
      leave: 'Leave and permits',
    },
  },

  messages: {
    search: 'Search by name, email or text',
    noMatches: 'No messages match that search.',
    selectOne: 'Select a message to read it.',
    reply: 'Reply',
    call: 'Call',
    back: 'All messages',
    email: 'Email',
    language: 'Language',
    readLabel: 'Read',
    title: 'Messages',
    subtitle: 'Enquiries from the website contact form',
    inbox: 'Inbox',
    archived: 'Archived',
    new: 'New',
    noSubject: 'No subject',
    empty: 'No messages.',
    emptyHint: 'When someone fills in the form on the website, it shows up here.',
    emptyArchived: 'No archived messages.',
    readBy: 'Read by {name} on {when}',
    wroteInItalian: 'Wrote in Italian',
    wroteInArabic: 'Wrote in Arabic',
    wroteInEnglish: 'Wrote in English',
    archive: 'Archive',
    restore: 'Restore',
  },
  nudge: {
    pushTitle: 'Turn on notifications',
    pushText: 'Job reminders, expiries and news on this device, even with the app closed.',
    pushOn: 'Turn on',
    dismiss: 'Not now',
  },
  notifications: {
    tab: 'Notifications',
    bell: 'Notifications',
    unread: '{count} unread',
    markAllRead: 'Mark all as read',
    empty: 'No notifications.',
    emptyHint: 'Reminders, expiries and news will show up here.',
    settingsLink: 'Notification settings',
    intro: 'Choose what you receive, how, and how far ahead. Applies to both the portal and the app.',
    groups: {
      schedule: 'Your schedule',
      documents: 'Your documents',
      equipment: 'Equipment you hold',
      office: 'For the office',
    },
    types: {
      job_start: { title: 'Job starting', hint: 'A reminder before each job you are assigned to starts.' },
      clock_out: { title: 'Remember to clock out', hint: 'If you are still clocked in after the job’s planned end.' },
      job_assigned: { title: 'New job assigned', hint: 'When the office assigns you to a job.' },
      job_changed: { title: 'Job moved or cancelled', hint: 'When one of your jobs changes time, or is cancelled.' },
      shift_adjusted: { title: 'Shift corrected by the office', hint: 'When the office changes the times on one of your shifts, with the reason.' },
      document_expiring: { title: 'Document expiring', hint: 'Certificates, courses and personal documents, before they expire.' },
      document_expired: { title: 'Document expired', hint: 'After the expiry date, if it has not been renewed.' },
      equipment_due: { title: 'Equipment due back', hint: 'Before the return date of equipment you are holding.' },
      equipment_overdue: { title: 'Equipment overdue', hint: 'When the return date has passed.' },
      staff_document_expiring: { title: 'Staff documents expiring', hint: 'Every employee’s documents, before they expire.' },
      missed_clock_in: { title: 'Missed clock-in', hint: 'When someone assigned to a job has not clocked in after it started.' },
      shift_flagged: { title: 'Irregular clock-in', hint: 'Clock-ins outside the site or outside hours, as they happen.' },
      new_enquiry: { title: 'New website enquiry', hint: 'When someone fills in the contact form.' },
      staff_equipment_overdue: { title: 'Equipment not returned', hint: 'A morning summary, when equipment is overdue.' },
      event_invited: { title: 'Invited to an appointment', hint: 'When the office adds you to an appointment.' },
      leave_decided: { title: 'Answer to a leave or permit request', hint: 'When your request is approved or rejected.' },
      leave_requested: { title: 'New leave or permit request', hint: 'When an employee asks for leave or a permit.' },
    },
    timing: {
      job_start: 'How far ahead',
      clock_out: 'How long after the planned end',
      document_expiring: 'How long before it expires',
      equipment_due: 'How long before the return date',
      staff_document_expiring: 'How long before it expires',
      missed_clock_in: 'How long after the start',
    },
    device: {
      title: 'Push notifications on this device',
      on: 'On: reminders arrive here even with the portal closed.',
      off: 'Get reminders on this device even with the portal closed.',
      denied: 'Notifications for this site are blocked in your browser settings. Unblock them there, then come back.',
      installFirst: 'On iPhone and iPad: tap Share → Add to Home Screen, open the portal from there, then turn them on.',
      unsupported: 'This browser cannot receive push notifications.',
      turnOn: 'Turn on',
      turnOff: 'Turn off',
      failed: 'Could not turn them on. Try again.',
    },
    multiple: 'You can pick more than one.',
    how: 'Also send by',
    alsoInBell: 'And always under the bell.',
    onlyInBell: 'Only under the bell: no email, no push notification.',
    channels: { in_app: 'Portal and app', email: 'Email', push: 'Push notification' },
    always: 'Always on',
    emailPending: 'Email sending is being set up — until then you will find these here.',
    pushMissing: 'For push notifications, turn them on for this device above, or install the HH Electrical Systems app on your phone.',
    minutes: '{count} min',
    hourOne: '1 hour',
    hours: '{count} hours',
    dayOne: '1 day',
    days: '{count} days',
    sameDay: 'On the day',
    atEnd: 'At the end',
    quiet: 'Do not disturb',
    quietHint: 'During these hours email and push wait; notifications still arrive here. Italian time.',
    quietOn: 'On',
    from: 'From',
    to: 'To',
    emailLanguage: 'Email language',
    emailLanguageHint: 'Here, notifications follow the language you have chosen; emails use this one.',
    save: 'Save preferences',
    saved: 'Preferences saved.',
    discard: 'Discard changes',
    on: 'On',
    off: 'Off',
  },
  account: {
    passkeyBlockedByApp: 'With the authenticator app on, you cannot sign in with a passkey: Amazon’s sign-in service does not allow both. A passkey (fingerprint, face or PIN) already counts as two-step verification, so you can turn the app off without losing security.',
    turnOffAppForPasskey: 'Turn off the app and use the passkey',
    passkeyReady: 'Done: you can now sign in with your passkey.',
    appDisablesPasskeys: 'With the authenticator app on, you will not be able to sign in with your passkeys (only with password and code). Continue?',
    tab: 'Account',
    tabRules: 'Clock-in rules',
    security: 'Sign-in and security',
    securityHint: 'How you get into the portal. None of these is required, but a passkey is both the easiest and the safest.',
    passkeys: 'Passkeys',
    passkeysHint: 'Sign in with your fingerprint, face or device PIN — no password, no codes. One for each device you use.',
    addPasskey: 'Add passkey',
    noPasskeys: 'No passkeys yet.',
    unnamed: 'Passkey',
    addedOn: 'Added {date}',
    remove: 'Remove',
    removeConfirm: 'Remove this passkey? That device will need your password again.',
    passkeyAdded: 'Passkey added.',
    unsupported: 'This browser does not support passkeys.',
    app: 'Authenticator app',
    appHint: 'After your password you will be asked for a 6-digit code. Not asked when you sign in with a passkey.',
    on: 'On',
    off: 'Off',
    setUp: 'Set up',
    turnOff: 'Turn off',
    turnOffConfirm: 'Turn off the authenticator app? A password alone protects the account less.',
    enabled: 'Authenticator app turned on.',
    disabled: 'Authenticator app turned off.',
    cancel: 'Cancel',
    password: 'Password',
    passwordHint: 'At least 12 characters, with an uppercase letter, a lowercase letter and a number.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    changePassword: 'Change password',
    passwordChanged: 'Password updated.',
    mismatch: 'The two passwords do not match.',
  },
};
